import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';

// Context Providers
import { ComplianceModeProvider } from './contexts/ComplianceModeContext';

// Eagerly loaded components (small, frequently used)
import Login from './components/Login';
import DemoLogin from './components/DemoLogin';
import BridgeAuth from './components/BridgeAuth';
import PlatformBridgeAuth from './components/PlatformBridgeAuth';
import AuthComplete from './components/AuthComplete';
import ApplianceSignIn from './components/ApplianceSignIn';
import Activate from './components/Activate';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy loaded components (large, code-split)
const UnifiedProjectDashboard = lazy(() => import('./components/UnifiedProjectDashboard'));
const ProjectDetail = lazy(() => import('./components/project/ProjectCommandCenter'));
const SystemModeling = lazy(() => import('./components/SystemModeling'));
const UnifiedRiskManagement = lazy(() => import('./components/UnifiedRiskManagement'));
const Reports = lazy(() => import('./components/Reports'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const HelpTrends = lazy(() => import('./components/HelpTrends'));
const ThreatIntelligence = lazy(() => import('./components/ThreatIntelligence'));
// The legacy CMMC page served a hardcoded report (2026-10-29); its URL now opens the Compliance Hub.
function LegacyCmmcRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/compliance/${id}`} replace />;
}
const SprsPage = lazy(() => import('./components/project/views/SprsPage'));
const PDRScoring = lazy(() => import('./components/PDRScoring'));
const ZeroTrustAssessment = lazy(() => import('./components/ZeroTrustAssessment'));
const UnifiedBudgetAndInvestment = lazy(() => import('./components/UnifiedBudgetAndInvestment'));
const RecommendationsImportDetails = lazy(() => import('./components/RecommendationsImportDetails'));
const UnifiedComplianceHub = lazy(() => import('./components/UnifiedComplianceHub'));
const UnifiedDataImport = lazy(() => import('./components/UnifiedDataImport'));
const ComplianceMonitoringPage = lazy(() => import('./components/compliance/monitoring/ComplianceMonitoringPage'));
const EvidenceLibrary = lazy(() => import('./components/EvidenceLibrary'));
const StandardAssessment = lazy(() => import('./components/StandardAssessment'));
const FoundationsIntegration = lazy(() => import('./components/FoundationsIntegration'));
const AIGovernance = lazy(() => import('./components/AIGovernance'));
const ContractAnalysis = lazy(() => import('./components/ContractAnalysis'));
const RmfJourneyModule = lazy(() => import('./components/project/rmf/RmfJourneyModule'));
const ParallaxTtxModule = lazy(() => import('./components/project/parallax/ParallaxTtxModule').then(m => ({ default: m.ParallaxTtxModule })));
const Fips199Workflow = lazy(() => import('./components/project/rmf/Fips199Workflow'));
const CrmInheritanceWorkflow = lazy(() => import('./components/project/rmf/CrmInheritanceWorkflow'));
const CuiScopingWorkflow = lazy(() => import('./components/project/rmf/CuiScopingWorkflow'));
const AssessmentWorkflow = lazy(() => import('./components/project/rmf/AssessmentWorkflow'));
const ImplementWorkflow = lazy(() => import('./components/project/rmf/ImplementWorkflow'));
const RarWorkflow = lazy(() => import('./components/project/rmf/RarWorkflow'));
const Soc2JourneyModule = lazy(() => import('./components/project/soc2/Soc2JourneyModule'));
const HipaaJourneyModule = lazy(() => import('./components/project/hipaa/HipaaJourneyModule'));
const ItarJourneyModule = lazy(() => import('./components/project/itar/ItarJourneyModule'));
const AiJourneyModule = lazy(() => import('./components/project/journey/journeyDefinitions/ai'));
const Soc2ScopeWorkflow = lazy(() => import('./components/project/soc2/Soc2ScopeWorkflow'));
const Soc2SystemDescriptionWorkflow = lazy(() => import('./components/project/soc2/Soc2SystemDescriptionWorkflow'));
const Soc2RiskAssessmentWorkflow = lazy(() => import('./components/project/soc2/Soc2RiskAssessmentWorkflow'));
const Soc2ControlsWorkflow = lazy(() => import('./components/project/soc2/Soc2ControlsWorkflow'));
const Soc2ComplementaryWorkflow = lazy(() => import('./components/project/soc2/Soc2ComplementaryWorkflow'));
const Soc2FinalAssemblyWorkflow = lazy(() => import('./components/project/soc2/Soc2FinalAssemblyWorkflow'));
const ProjectWizard = lazy(() => import('./components/ProjectWizard'));
const FoundationsOnboardingWizard = lazy(() => import('./components/FoundationsOnboardingWizard'));
const TemplateLibrary = lazy(() => import('./components/TemplateLibrary'));
const CompliancePackages = lazy(() => import('./components/CompliancePackages'));
const TrustCenter = lazy(() => import('./components/TrustCenter'));
const TrustCenterSettings = lazy(() => import('./components/TrustCenterSettings'));
const IntegrationsManager = lazy(() => import('./components/IntegrationsManager'));
const OverInvestmentDetails = lazy(() => import('./components/OverInvestmentDetails'));
const CostOptimizationDashboard = lazy(() => import('./components/CostOptimizationDashboard'));
// No nav entry — flag-off (POSTURE_PRIORITIZATION_ENABLED) means no discoverable
// entry point; reachable only by direct URL until slice 2 adds the Brief drill-through.
const PrioritizationView = lazy(() => import('./components/PrioritizationView'));
const CorporateProjectRollup = lazy(() => import('./components/CorporateProjectRollup'));
const MSSPPortfolioDashboard = lazy(() => import('./components/MSSPPortfolioDashboard'));
const Settings = lazy(() => import('./components/Settings'));
const OrganizationUsers = lazy(() => import('./components/OrganizationUsers'));
const AdminConsole = lazy(() => import('./components/AdminConsole'));
const IrisQuality = lazy(() => import('./components/IrisQuality'));
const Documents = lazy(() => import('./components/Documents'));
const RedirectV1ToV2 = lazy(() => import('./components/RedirectV1ToV2'));
const BillingReturn = lazy(() => import('./components/billing/BillingReturn'));
// RMF Pre-Adjudication View — route-reachable; NOT linked in primary nav until
// RMF_PREADJUDICATION_ENABLED flag flips.  The View handles backend 404 gracefully.
const RmfPreAdjudication = lazy(() => import('./components/RmfPreAdjudication'));

// Loading fallback component
const LoadingFallback = () => (
  <Box
    role="status"
    aria-label="Loading content"
    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }}
  >
    <CircularProgress aria-hidden="true" />
  </Box>
);

// Theme Configuration for Risk Aperture PRISM — Dark Glassmorphism
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00d9ff', // Cyber Aqua
      light: '#64ffda',
      dark: '#0066ff',
    },
    secondary: {
      main: '#64ffda', // Light Aqua
      light: '#a7ffeb',
      dark: '#00bfa5',
    },
    error: {
      main: '#ff4444',
    },
    warning: {
      main: '#ffa500',
    },
    success: {
      main: '#00ff88',
    },
    info: {
      main: '#00d9ff',
    },
    background: {
      default: '#0a192f', // Navy
      paper: '#172a45', // Deep Blue
    },
    text: {
      primary: '#e6f1ff',
      secondary: '#a8b2cc',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    action: {
      hover: 'rgba(0, 217, 255, 0.08)',
      selected: 'rgba(0, 217, 255, 0.16)',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#e6f1ff',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#e6f1ff',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#ccd6f6',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#ccd6f6',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#a8b2cc',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#a8b2cc',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0a192f',
        },
      },
    },
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0, 0, 0, 0.3)',
    '0 2px 6px rgba(0, 0, 0, 0.3)',
    '0 4px 12px rgba(0, 0, 0, 0.3)',
    '0 8px 24px rgba(0, 0, 0, 0.3)',
    '0 12px 32px rgba(0, 0, 0, 0.3)',
    '0 16px 40px rgba(0, 0, 0, 0.3)',
    ...Array(18).fill('0 16px 40px rgba(0, 0, 0, 0.3)'),
  ] as any,
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = sessionStorage.getItem('prism_token') || localStorage.getItem('prism_token');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ComplianceModeProvider defaultMode="FEDERAL">
          <Router>
            <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/demo" element={<DemoLogin />} />
                <Route path="/auth/bridge" element={<BridgeAuth />} />
                <Route path="/auth/platform-bridge" element={<PlatformBridgeAuth />} />
                <Route path="/auth/complete" element={<AuthComplete />} />
                <Route path="/appliance/sign-in" element={<ApplianceSignIn />} />
                <Route path="/activate" element={<Activate />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/trust/:slug" element={<TrustCenter />} />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="projects-v2" element={<UnifiedProjectDashboard />} />
                  <Route path="projects-v2/new" element={<ProjectWizard />} />
                  <Route path="projects-v2/:id" element={<ProjectDetail />} />
                  <Route path="projects-v2/:id/wizard" element={<ProjectWizard />} />
                  <Route path="projects-v2/:id/foundations-onboarding" element={<FoundationsOnboardingWizard />} />
                  <Route path="compliance-monitoring/:projectId" element={<ComplianceMonitoringPage />} />

                  {/* v2 tool / module / RMF routes — canonical paths */}
                  <Route path="projects-v2/:projectId/nist-csf" element={<PDRScoring />} />
                  <Route path="projects-v2/:projectId/budget" element={<UnifiedBudgetAndInvestment />} />
                  <Route path="projects-v2/:projectId/recommendations-import" element={<RecommendationsImportDetails />} />
                  <Route path="projects-v2/:id/modeling" element={<SystemModeling />} />
                  <Route path="projects-v2/:id/risk" element={<UnifiedRiskManagement />} />
                  <Route path="projects-v2/:id/threat-intelligence" element={<ThreatIntelligence />} />
                  <Route path="projects-v2/:id/cmmc" element={<LegacyCmmcRedirect />} />
                  {/* SPRS has a URL so the CMMC shell (which replaces the
                      sidebar) can reach it; the sidebar view is unchanged. */}
                  <Route path="projects-v2/:id/sprs" element={<SprsPage />} />
                  <Route path="projects-v2/:id/zero-trust" element={<ZeroTrustAssessment />} />
                  <Route path="projects-v2/:projectId/import" element={<UnifiedDataImport />} />
                  <Route path="projects-v2/:projectId/evidence" element={<EvidenceLibrary />} />
                  <Route path="projects-v2/:projectId/foundations" element={<FoundationsIntegration />} />
                  <Route path="projects-v2/:projectId/ai-governance" element={<AIGovernance />} />
                  <Route path="projects-v2/:projectId/contract-analysis" element={<ContractAnalysis />} />
                  <Route path="projects-v2/:id/rmf" element={<RmfJourneyModule />} />
                  <Route path="projects-v2/:id/rmf/step/1" element={<Fips199Workflow />} />
                  <Route path="projects-v2/:id/rmf/step/2" element={<CrmInheritanceWorkflow />} />
                  <Route path="projects-v2/:id/rmf/step/2/scoping" element={<CuiScopingWorkflow />} />
                  <Route path="projects-v2/:id/rmf/step/3" element={<ImplementWorkflow />} />
                  <Route path="projects-v2/:id/rmf/step/4" element={<AssessmentWorkflow />} />
                  <Route path="projects-v2/:id/rmf/step/4/rar" element={<RarWorkflow />} />
                  {/* SOC 2 SSP Journey (parallel to RMF, per SOC2_SSP_JOURNEY_SCOPING_SPEC.md v1.1) */}
                  <Route path="projects-v2/:id/soc2" element={<Soc2JourneyModule />} />
                  <Route path="projects-v2/:id/soc2/step/scope" element={<Soc2ScopeWorkflow />} />
                  <Route path="projects-v2/:id/soc2/step/system-description" element={<Soc2SystemDescriptionWorkflow />} />
                  <Route path="projects-v2/:id/soc2/step/risk-assessment" element={<Soc2RiskAssessmentWorkflow />} />
                  <Route path="projects-v2/:id/soc2/step/controls" element={<Soc2ControlsWorkflow />} />
                  <Route path="projects-v2/:id/soc2/step/complementary" element={<Soc2ComplementaryWorkflow />} />
                  <Route path="projects-v2/:id/soc2/step/final-assembly" element={<Soc2FinalAssemblyWorkflow />} />
                  {/* HIPAA Security Rule Journey — single route; step bodies embed inline
                      (internal assessors never leave the journey) per the persona→surface model */}
                  <Route path="projects-v2/:id/hipaa" element={<HipaaJourneyModule />} />
                  {/* ITAR Program Package Journey — 2-step (Determination → TCP); ITAR owns its substance */}
                  <Route path="projects-v2/:id/itar" element={<ItarJourneyModule />} />
                  {/* AI Conformance Journey — self-guided 6-step wizard (govern, inventory,
                      characterize, cui_reachability, conformance, report); thin JourneyShell binding */}
                  <Route path="projects-v2/:id/ai-journey" element={<AiJourneyModule />} />
                  {/* RMF Pre-Adjudication Readiness View — flag-gated; not in primary nav */}
                  <Route path="projects-v2/:projectId/rmf-preadj" element={<RmfPreAdjudication />} />
                  {/* Parallax TTX Journey — 4-step facilitator UI; flag-gated PARALLAX_TTX_ENABLED */}
                  <Route path="projects-v2/:id/parallax-ttx" element={<ParallaxTtxModule />} />
                  <Route path="projects-v2/:projectId/templates" element={<TemplateLibrary />} />
                  <Route path="projects-v2/:projectId/packages" element={<CompliancePackages />} />
                  <Route path="projects-v2/:projectId/over-investment" element={<OverInvestmentDetails />} />
                  <Route path="projects-v2/:projectId/cost-optimization" element={<CostOptimizationDashboard />} />
                  {/* Posture → Prioritization slice 1 — no nav entry by design; direct URL only */}
                  <Route path="projects-v2/:projectId/prioritization" element={<PrioritizationView />} />

                  {/* Legacy /projects/* paths — redirect everything to /projects-v2 to keep bookmarks working */}
                  <Route path="projects" element={<Navigate to="/projects-v2" replace />} />
                  <Route path="projects/:id/*" element={<RedirectV1ToV2 />} />
                  <Route path="compliance" element={<UnifiedComplianceHub />} />
                  <Route path="compliance/:projectId" element={<UnifiedComplianceHub />} />
                  <Route path="compliance/:projectId/:standardId" element={<StandardAssessment />} />
                  <Route path="risk" element={<UnifiedRiskManagement />} />
                  <Route path="integrations" element={<IntegrationsManager />} />
                  <Route path="corporate-dashboard" element={<CorporateProjectRollup />} />
                  <Route path="mssp-portfolio" element={<MSSPPortfolioDashboard />} />
                  <Route path="trust-center" element={<TrustCenterSettings />} />
                  <Route path="documents" element={<Documents />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="help" element={<HelpPage />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="settings/users" element={<OrganizationUsers />} />
                  <Route path="admin" element={<AdminConsole />} />
                  <Route path="admin/iris/quality" element={<IrisQuality />} />
                  <Route path="admin/help/trends" element={<HelpTrends />} />
                  <Route path="billing/return" element={<BillingReturn />} />
                </Route>
                </Routes>
              </Suspense>
            </Box>
          </Router>
        </ComplianceModeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
